/**
 * Saved contacts are Tour Planner `userLocations` (name + address + lat/lng).
 * Calendar Map and Contacts show the ones near a show or travel stop.
 */

export const SAVED_CONTACT_RADIUS_MILES = 50;
/** How many nearest contacts the sidebar shows before a search. */
export const SAVED_CONTACT_PREVIEW_COUNT = 5;

function toRad(degrees) {
  return (degrees * Math.PI) / 180;
}

export function milesBetween(lat1, lng1, lat2, lng2) {
  const radius = 3958.8;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * radius * Math.asin(Math.min(1, Math.sqrt(a)));
}

export function stopsFromTour(tour) {
  const stops = [];
  (tour?.days || []).forEach((day) => {
    (day?.venues || []).forEach((venue) => {
      const lat = Number(venue?.latitude ?? venue?.lat);
      const lng = Number(venue?.longitude ?? venue?.lng ?? venue?.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return;
      stops.push({
        lat,
        lng,
        label: String(venue.city || venue.name || venue.venue || 'Stop').trim() || 'Stop',
        dateKey: String(day.scheduledDate || day.date || '').trim(),
      });
    });
  });
  return stops;
}

export function contactCoords(contact) {
  const lat = Number(contact?.latitude ?? contact?.lat);
  const lng = Number(contact?.longitude ?? contact?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

export function nearestStop(contact, stops) {
  const coords = contactCoords(contact);
  if (!coords) return null;
  let best = null;
  (stops || []).forEach((stop) => {
    if (!Number.isFinite(stop?.lat) || !Number.isFinite(stop?.lng)) return;
    const miles = milesBetween(coords.lat, coords.lng, stop.lat, stop.lng);
    if (!best || miles < best.miles) best = { miles, stop };
  });
  return best;
}

export function filterContactsNearStops(contacts, stops, radiusMiles = SAVED_CONTACT_RADIUS_MILES) {
  return (contacts || []).filter((contact) => {
    const near = nearestStop(contact, stops);
    return Boolean(near && near.miles <= radiusMiles);
  });
}

export function searchSavedContacts(contacts, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return contacts || [];
  return (contacts || []).filter((contact) => {
    const blob = [contact?.name, contact?.address, contact?.notes, contact?.category]
      .map((part) => String(part || '').toLowerCase())
      .join(' ');
    return blob.includes(q);
  });
}

function sortByNearest(contacts, stops) {
  return annotateSavedContacts(contacts, stops).sort((a, b) => {
    const aMiles = a.nearest ? a.nearest.miles : Number.POSITIVE_INFINITY;
    const bMiles = b.nearest ? b.nearest.miles : Number.POSITIVE_INFINITY;
    if (aMiles !== bMiles) return aMiles - bMiles;
    return String(a.name || '').localeCompare(String(b.name || ''));
  });
}

/**
 * A search returns every match. With no query, the nearest contacts (or the
 * first few, when nothing is mapped) are returned, capped by `limit`.
 */
export function visibleSavedContacts(contacts, {
  query = '',
  stops = [],
  dateKey = '',
  radiusMiles = SAVED_CONTACT_RADIUS_MILES,
  limit = 0,
} = {}) {
  const list = contacts || [];
  if (String(query || '').trim()) return searchSavedContacts(list, query);
  let pool = list;
  if (stops.length) {
    const dayStops = dateKey ? stops.filter((stop) => stop.dateKey === dateKey) : [];
    pool = filterContactsNearStops(list, dayStops.length ? dayStops : stops, radiusMiles);
  }
  const sorted = sortByNearest(pool, stops);
  const cap = Number(limit) || 0;
  return cap > 0 ? sorted.slice(0, cap) : sorted;
}

export function annotateSavedContacts(contacts, stops) {
  return (contacts || []).map((contact) => ({
    ...contact,
    nearest: nearestStop(contact, stops),
  }));
}
